/*
   The review's detector settings, at intake.

   Collapsed by default with a one-line summary, because the right answer is
   almost always Automatic: the engagement's profile, derived from that
   client's own labelled data, or the deployment default when there is none.
   Opening it is for the review that needs something else.
*/

import { ChevronDown } from "lucide-react"
import * as React from "react"

import {
  DetectorProfileFields,
  profileSummary,
  type DetectorProfile,
  type DetectorProfileOptions,
} from "@/components/domain/detector-profile"
import { Skeleton } from "@/components/ui/alert"
import { cn } from "@/lib/utils"

export interface DetectorSettingsGroupProps {
  /** Undefined while the options load. */
  options: DetectorProfileOptions | undefined
  /** The options route failed: there are no labels to offer, so there is no control. */
  unavailable: boolean
  value: DetectorProfile
  onChange: (next: DetectorProfile) => void
}

export function DetectorSettingsGroup({
  options,
  unavailable,
  value,
  onChange,
}: DetectorSettingsGroupProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const regionId = React.useId()

  if (unavailable) {
    return (
      <p className="mt-5 text-caption leading-normal text-text-3">
        Detector settings could not be loaded. The review runs on Automatic for each.
      </p>
    )
  }

  return (
    <div className="mt-5 rounded-lg border border-border">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => {
          setOpen((v) => !v)
        }}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3.5 py-2.5 text-left",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
          "hover:bg-surface-sunken",
        )}
      >
        <span className="text-body-sm font-semibold text-text">Detector settings</span>
        <span className="min-w-0 flex-1 truncate text-caption text-text-3">
          {options ? profileSummary(value, options) : <Skeleton className="h-3 w-28" />}
        </span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-text-3 transition-transform duration-120 motion-reduce:transition-none",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      <div id={regionId} hidden={!open} className="border-t border-border px-3.5 py-3">
        <DetectorProfileFields
          options={options}
          value={value}
          onChange={onChange}
          autoMeans="engagement-then-default"
        />
      </div>
    </div>
  )
}
